import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { WebsiteBlock } from "../../../cores/websitePage/interfaces/WebsiteBlock";
import "./NavbarBlock.scss";

interface Props {
  block: WebsiteBlock;
  isEditing?: boolean;
  onUpdate?: (block: WebsiteBlock) => void;
}

export function NavbarBlock({ block, isEditing, onUpdate }: Props) {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = block.links || [];
  const bg = block.backgroundColor || "#ffffff";
  const menuBg = block.menuBackgroundColor || bg;
  const color = block.textColor || "#0f172a";
  const accent = block.accentColor || "#4f46e5";

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, closeMenu]);

  const editable = (value: string | undefined, commit: (v: string) => void) => {
    if (!isEditing || !onUpdate) return value;
    return (
      <span
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onBlur={(e) => {
          const v = e.currentTarget.innerText.replace(/\n+$/, "");
          if (v !== (value || "")) commit(v);
        }}
        style={{ outline: "none", cursor: "text" }}
      >
        {value}
      </span>
    );
  };

  return (
    <header
      className={`wp-navbar ${block.sticky !== false ? "wp-navbar--sticky" : ""}`}
      style={{ background: bg, color, borderColor: `${color}20` }}
    >
      {menuOpen && (
        <button
          type="button"
          className="wp-navbar__overlay"
          aria-label={intl.formatMessage({ id: "app.websitePage.navbar.closeMenu" })}
          onClick={closeMenu}
        />
      )}

      <div className="wp-navbar__inner">
        <a href="#inicio" className="wp-navbar__brand" onClick={(e) => isEditing && e.preventDefault()}>
          <span className="wp-navbar__logo" style={{ background: `${accent}18`, color: accent }}>
            {block.logoUrl ? (
              <img src={block.logoUrl} alt={block.brandName || "Logo"} />
            ) : (
              editable(block.logoInitial || block.brandName?.charAt(0) || "M", (v) =>
                onUpdate?.({ ...block, logoInitial: v.slice(0, 2) })
              )
            )}
          </span>
          <span className="wp-navbar__brand-name">
            {editable(block.brandName, (v) => onUpdate?.({ ...block, brandName: v }))}
          </span>
        </a>

        <button
          type="button"
          className="wp-navbar__menu-btn"
          aria-label={
            menuOpen
              ? intl.formatMessage({ id: "app.websitePage.navbar.closeMenu" })
              : intl.formatMessage({ id: "app.websitePage.navbar.openMenu" })
          }
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((open) => !open);
          }}
        >
          <span className="wp-navbar__menu-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>

        <nav
          className={`wp-navbar__nav ${menuOpen ? "wp-navbar__nav--open" : ""}`}
          aria-label={intl.formatMessage({ id: "app.websitePage.navbar.mainNav" })}
          style={{ background: menuBg, color }}
        >
          {links.map((link, index) => (
            <a
              key={`${link.href}-${index}`}
              href={link.href || "#"}
              onClick={(e) => {
                if (isEditing) e.preventDefault();
                else closeMenu();
              }}
            >
              {isEditing && onUpdate ? (
                <span
                  contentEditable
                  suppressContentEditableWarning
                  spellCheck={false}
                  onBlur={(e) => {
                    const label = e.currentTarget.innerText.replace(/\n+$/, "");
                    if (label === link.label) return;
                    const nextLinks = [...links];
                    nextLinks[index] = { ...link, label };
                    onUpdate({ ...block, links: nextLinks });
                  }}
                >
                  {link.label}
                </span>
              ) : (
                link.label
              )}
            </a>
          ))}
          {block.ctaLabel && (
            <a
              className="wp-navbar__cta"
              href={block.ctaHref || "#"}
              style={{ background: accent, color: "#fff" }}
              onClick={(e) => {
                if (isEditing) e.preventDefault();
                else closeMenu();
              }}
            >
              {editable(block.ctaLabel, (v) => onUpdate?.({ ...block, ctaLabel: v }))}
            </a>
          )}
        </nav>
      </div>
    </header>
  );
}
